import type { CSSProperties } from 'react';
import { SIZE_SCALES, TONE_PALETTES } from './button-presets';
import type { ButtonPreset, ButtonTone } from './button-presets';
import {
  BUTTON_BORDER_VARIABLE,
  BUTTON_FILL_ACTIVE_VARIABLE,
  BUTTON_FILL_HOVER_VARIABLE,
  BUTTON_FILL_VARIABLE,
  BUTTON_INK_HOVER_VARIABLE,
  BUTTON_INK_VARIABLE,
  BUTTON_RING_VARIABLE,
  toneColor,
} from './button-css-variables';

const asVars = (vars: Record<string, string>): CSSProperties => vars as CSSProperties;

/**
 * The rules that stop a native `<button>` looking like a native `<button>`.
 *
 * Two of these do most of the work. `appearance: none` removes the platform
 * chrome iOS Safari still paints on form controls, and `fontFamily: inherit`
 * opts out of the browser's refusal to inherit typography into form controls —
 * which is the single biggest reason an unstyled button looks foreign on a
 * designed page. Inheriting means the button picks up the customer's brand
 * typeface with no configuration at all.
 */
const structuralStyles: CSSProperties = {
  appearance: 'none',
  WebkitAppearance: 'none',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  textDecoration: 'none',
  cursor: 'pointer',
  userSelect: 'none',
  WebkitUserSelect: 'none',
  verticalAlign: 'middle',
  borderStyle: 'solid',
  fontFamily: 'inherit',
  fontWeight: 600,
  lineHeight: 1.25,
  // Eases the library stylesheet's :hover / :active colour change.
  transition: 'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
};

const sizeStyles = (preset: ButtonPreset): CSSProperties => {
  const scale = SIZE_SCALES[preset.size];
  return {
    fontSize: scale.fontSize,
    minHeight: scale.minHeight,
    // Logical properties so a right-to-left page mirrors correctly.
    paddingInline: scale.paddingInline,
    paddingBlock: scale.paddingBlock,
    borderRadius: scale.borderRadius,
    gap: scale.gap,
  };
};

/**
 * Every variant keeps a 1px border, transparent where it is not wanted, so
 * switching preset never changes the button's box and never nudges the layout
 * around it.
 */
const variantStyles = (preset: ButtonPreset): CSSProperties => {
  const palette = TONE_PALETTES[preset.tone];
  const tone: ButtonTone = preset.tone;

  switch (preset.variant) {
    case 'solid':
      return {
        borderWidth: '1px',
        backgroundColor: toneColor(tone, '', palette.base),
        borderColor: toneColor(tone, '', palette.base),
        color: toneColor(tone, 'contrast', palette.contrast),
      };
    case 'soft':
      return {
        borderWidth: '1px',
        backgroundColor: toneColor(tone, 'soft', palette.soft),
        borderColor: 'transparent',
        color: toneColor(tone, 'soft-contrast', palette.softContrast),
      };
    case 'outline':
      return {
        borderWidth: '1px',
        backgroundColor: 'transparent',
        borderColor: toneColor(tone, 'border', palette.border),
        color: toneColor(tone, '', palette.base),
      };
    case 'ghost':
      return {
        borderWidth: '1px',
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        color: toneColor(tone, '', palette.base),
      };
    case 'link':
    default:
      // Reads as prose, not as a control: the size scale's box is stripped
      // back off so it can sit in a sentence without a padded hit area.
      return {
        borderWidth: 0,
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        color: toneColor(tone, '', palette.base),
        minHeight: 0,
        paddingInline: 0,
        paddingBlock: 0,
        borderRadius: 0,
        textDecoration: 'underline',
        textUnderlineOffset: '0.2em',
      };
  }
};

/**
 * Per-instance colour slots the library stylesheet reads. Tone tokens stay
 * in the `var(--rulecms-button-brand, fallback)` form so a host `:root`
 * rebrand still wins.
 */
const variantCssVariables = (preset: ButtonPreset): CSSProperties => {
  const palette = TONE_PALETTES[preset.tone];
  const tone: ButtonTone = preset.tone;
  const ink = toneColor(tone, '', palette.base);
  const inkHover = toneColor(tone, 'hover', palette.hover);
  const contrast = toneColor(tone, 'contrast', palette.contrast);

  switch (preset.variant) {
    case 'solid':
      return asVars({
        [BUTTON_FILL_VARIABLE]: toneColor(tone, '', palette.base),
        [BUTTON_FILL_HOVER_VARIABLE]: toneColor(tone, 'hover', palette.hover),
        [BUTTON_FILL_ACTIVE_VARIABLE]: toneColor(tone, 'active', palette.active),
        [BUTTON_INK_VARIABLE]: contrast,
        [BUTTON_INK_HOVER_VARIABLE]: contrast,
        [BUTTON_BORDER_VARIABLE]: toneColor(tone, '', palette.base),
        [BUTTON_RING_VARIABLE]: ink,
      });
    case 'soft':
      return asVars({
        [BUTTON_FILL_VARIABLE]: toneColor(tone, 'soft', palette.soft),
        [BUTTON_FILL_HOVER_VARIABLE]: toneColor(tone, 'soft-hover', palette.softHover),
        [BUTTON_FILL_ACTIVE_VARIABLE]: `color-mix(in srgb, ${toneColor(tone, 'soft-hover', palette.softHover)} 88%, black)`,
        [BUTTON_INK_VARIABLE]: toneColor(tone, 'soft-contrast', palette.softContrast),
        [BUTTON_INK_HOVER_VARIABLE]: toneColor(tone, 'soft-contrast', palette.softContrast),
        [BUTTON_BORDER_VARIABLE]: 'transparent',
        [BUTTON_RING_VARIABLE]: ink,
      });
    case 'outline':
      return asVars({
        [BUTTON_FILL_VARIABLE]: 'transparent',
        [BUTTON_FILL_HOVER_VARIABLE]: toneColor(tone, 'soft', palette.soft),
        [BUTTON_FILL_ACTIVE_VARIABLE]: toneColor(tone, 'soft-hover', palette.softHover),
        [BUTTON_INK_VARIABLE]: ink,
        [BUTTON_INK_HOVER_VARIABLE]: inkHover,
        [BUTTON_BORDER_VARIABLE]: toneColor(tone, 'border', palette.border),
        [BUTTON_RING_VARIABLE]: ink,
      });
    case 'ghost':
      return asVars({
        [BUTTON_FILL_VARIABLE]: 'transparent',
        [BUTTON_FILL_HOVER_VARIABLE]: toneColor(tone, 'soft', palette.soft),
        [BUTTON_FILL_ACTIVE_VARIABLE]: toneColor(tone, 'soft-hover', palette.softHover),
        [BUTTON_INK_VARIABLE]: ink,
        [BUTTON_INK_HOVER_VARIABLE]: inkHover,
        [BUTTON_BORDER_VARIABLE]: 'transparent',
        [BUTTON_RING_VARIABLE]: ink,
      });
    case 'link':
    default:
      return asVars({
        [BUTTON_FILL_VARIABLE]: 'transparent',
        [BUTTON_FILL_HOVER_VARIABLE]: 'transparent',
        [BUTTON_FILL_ACTIVE_VARIABLE]: 'transparent',
        [BUTTON_INK_VARIABLE]: ink,
        [BUTTON_INK_HOVER_VARIABLE]: inkHover,
        [BUTTON_BORDER_VARIABLE]: 'transparent',
        [BUTTON_RING_VARIABLE]: ink,
      });
  }
};

const COLOR_STYLE_KEYS = ['backgroundColor', 'color', 'borderColor'] as const;

/**
 * The preset's contribution to the button's inline style, including resting
 * colours as real CSS properties. The composer picker uses this so a swatch
 * looks right without the library stylesheet.
 *
 * Callers spread column `styleProps` *after* this, which is what makes the
 * preset a set of defaults rather than a set of rules: anything the author
 * touched in Column Styles lands later in the object and wins.
 */
export function buildButtonPresetStyles(preset: ButtonPreset): CSSProperties {
  return {
    ...structuralStyles,
    ...sizeStyles(preset),
    ...variantStyles(preset),
    ...variantCssVariables(preset),
  };
}

/**
 * What `RButton` puts on the element. Same as the picker styles, minus the
 * colour properties the library stylesheet owns — those stay on the element
 * only as custom properties so `:hover` can change them.
 */
export function buildButtonRenderStyles(preset: ButtonPreset): CSSProperties {
  const styles = { ...buildButtonPresetStyles(preset) };
  for (const key of COLOR_STYLE_KEYS) {
    delete styles[key];
  }
  return styles;
}

/**
 * Column styles still win on everything except `backgroundColor`, which is
 * remapped onto the fill slots so hover/active follow the authored colour
 * instead of the preset's blue-family hover.
 */
export function applyButtonColumnStyles(
  styleProps?: CSSProperties | { [key: string]: unknown }
): CSSProperties {
  if (!styleProps) {
    return {};
  }
  const { backgroundColor, ...rest } = styleProps as CSSProperties & {
    backgroundColor?: string;
  };
  if (backgroundColor === undefined || backgroundColor === '') {
    return rest;
  }
  return {
    ...rest,
    ...asVars({
      [BUTTON_FILL_VARIABLE]: backgroundColor,
      [BUTTON_FILL_HOVER_VARIABLE]: `color-mix(in srgb, ${backgroundColor} 82%, black)`,
      [BUTTON_FILL_ACTIVE_VARIABLE]: `color-mix(in srgb, ${backgroundColor} 70%, black)`,
    }),
  };
}

/** Applied on top when the button is disabled, before column styles. */
export const disabledStyles: CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
};
